import {
	Component,
	OnInit,
	ViewChild,
	ElementRef,
	AfterViewInit,
} from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { ProfileService } from 'src/app/pages/app/profile/services/profile.service';
import { first } from 'rxjs/operators';

declare var Stripe: any;

@Component({
	selector: 'app-payment-card',
	templateUrl: './payment-card.component.html',
	styleUrls: ['./payment-card.component.scss'],
})
export class PaymentCardComponent implements OnInit, AfterViewInit {
	@ViewChild('cardElement') cardElementRef!: ElementRef;

	stripe: any;
	cardElement: any;
	clientSecret: string | null = null;
	private userName: string | null = null;

	showForm: boolean = false;
	isLoading: boolean = false;
	isProcessing: boolean = false;
	successMessage: string = '';
	errorMessage: string = '';

	constructor(private http: HttpClient, private profileService: ProfileService) {}

	ngOnInit() {
		this.loadStripeScript();
		this.profileService.loadProfileData().pipe(first()).subscribe((profile: any) => {
			if (profile?.data?.email) {
				this.userName = profile.data.email.split('@')[0];
			}
		});
	}

	ngAfterViewInit() {
		// We cannot mount the card element until it's rendered.
	}

	// Load Stripe.js script dynamically
	private loadStripeScript() {
		if (!window.document.getElementById('stripe-script')) {
			const script = window.document.createElement('script');
			script.id = 'stripe-script';
			script.type = 'text/javascript';
			script.src = 'https://js.stripe.com/v3/';
			script.onload = () => {
				this.stripe = Stripe(environment.stripe.publishableKey);
			};
			window.document.body.appendChild(script);
		} else if ((window as any).Stripe) {
			this.stripe = Stripe(environment.stripe.publishableKey);
		}
	}

	// User Dashboard State: "Update Card" Step 1
	startUpdateCard() {
		this.showForm = true;
		this.isLoading = true;
		this.errorMessage = '';
		this.successMessage = '';

		// Step 1: Create SetupIntent from Backend
		this.http
			.post<{ clientSecret: string }>(
				environment.url.payment + 'subscription/setup-intent',
				{},
			)
			.subscribe({
				next: (response: any) => {
					this.clientSecret = response.clientSecret;
					this.isLoading = false;
					// Step 2: Initialize Stripe Element once we have the secret
					this.initStripeElement();
				},
				error: (err: any) => {
					this.isLoading = false;
					this.errorMessage = 'Failed to initialize payment method update.';
					console.error(err);
				},
			});
	}

	private initStripeElement() {
		if (!this.stripe) {
			this.errorMessage = 'Stripe has not been loaded correctly.';
			return;
		}

		// Give Angular a moment to render the card-element div (ngIf showForm)
		setTimeout(() => {
			const elements = this.stripe.elements();
			this.cardElement = elements.create('card');
			this.cardElement.mount(this.cardElementRef.nativeElement);

			this.cardElement.on('change', (event: any) => {
				if (event.error) {
					this.errorMessage = event.error.message;
				} else {
					this.errorMessage = '';
				}
			});
		}, 0);
	}

	// Save Card Event Step 4
	async confirmCard() {
		if (!this.stripe || !this.cardElement || !this.clientSecret) {
			return;
		}

		this.isProcessing = true;
		this.errorMessage = '';
		this.successMessage = '';

		const paymentMethodData: any = { card: this.cardElement };
		if (this.userName) {
			paymentMethodData.billing_details = { name: this.userName };
		}

		const { setupIntent, error } = await this.stripe.confirmCardSetup(
			this.clientSecret,
			{ payment_method: paymentMethodData },
		);

		if (error) {
			// Step 4 Error handling (User cancelled 3DS or declined)
			this.isProcessing = false;
			this.errorMessage = 'Card verification failed: ' + error.message;
		} else if (setupIntent && setupIntent.status === 'succeeded') {
			// Step 5: Sync Backend
			const paymentMethodId = setupIntent.payment_method;
			this.updateBackendPaymentMethod(paymentMethodId);
		}
	}

	private updateBackendPaymentMethod(paymentMethodId: string) {
		this.http
			.post<{ status: string; count: number }>(
				environment.url.payment + '/subscription/update-payment-method',
				{
					paymentMethodId: paymentMethodId,
				},
			)
			.subscribe({
				next: (res: any) => {
					this.isProcessing = false;
					this.successMessage = `Your card for all active subscriptions has been updated! (Updated ${res.count} active subscriptions)`;
					// Optionally hide the form after success
					this.showForm = false;
					if (this.cardElement) {
						this.cardElement.unmount();
					}
				},
				error: (err: any) => {
					this.isProcessing = false;
					this.errorMessage =
						'Card was verified, but failed to sync with our servers.';
					console.error(err);
				},
			});
	}
}
